import React from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../../controls/Heading/Heading';
import { CTAButton } from '../../../controls/ButtonBase';
import { useLocalization, useLocalizationExtension } from '../../../context/Localization/localization';
import { Spacer } from '../../../controls/Spacer/Spacer';
import { startSetup, verifyPassword } from '../../../helpers/LinuxKiosk';
import { useFullReset } from '../../../context/Kiosk/kiosk';
import { nameof } from '../../../helpers/nameof';
import { GMFormPasswordField } from '../../../controls/TextField';
import { KeyboardLayouts } from '../../../controls/SimpleKeyboard/Layout';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { GMForm, GMFormWrapper } from '../../../controls/Form';

export const SetupLogin = () => {
    const initialValues: { password?: string; } = {};
    const Localization = useLocalization();
    const { formatRequired, formatInvalid } = useLocalizationExtension();
    const [passwordInvalid, setPasswordInvalid] = React.useState(false);
    const navigate = useNavigate();
    const reset = useFullReset();

    return (
        <Formik
            initialValues={initialValues}
            validate={async (values) => {
                if (passwordInvalid) {
                    return { [nameof<typeof initialValues>('password')]: formatInvalid(Localization.Password) };
                }
                else {
                    return await Yup.object()
                        .shape({
                            [nameof<typeof initialValues>('password')]: Yup.string()
                                .required(formatRequired(Localization.Password))
                                .typeError(formatInvalid(Localization.Password)),
                        })
                        .validate(values)
                        .then(
                            () => ({}),
                            (error) => ({ [error.path]: error.message }),
                        );
                }
            }}
            onSubmit={async (values, { setFieldError, resetForm }) => {
                if (!values.password) {
                    setPasswordInvalid(true);
                    setFieldError(nameof<typeof initialValues>('password'), formatInvalid(Localization.Password));

                    return;
                }

                const result = await verifyPassword(values.password);

                if (!result) {
                    setPasswordInvalid(true);
                    setFieldError(nameof<typeof initialValues>('password'), formatInvalid(Localization.Password));

                    return;
                }
                else {
                    resetForm();
                    startSetup();
                    reset();
                }
            }}
        >
            <GMForm
                heading={
                    <Heading
                        primaryHeading={Localization.SetupDashboardLogin}
                        secondaryHeading={Localization.EnterPasswordToAccessTheSetupDashboard}
                        showMustache
                        roundedIcon={
                            <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                                <GMSizedIcon iconType={'Settings'} iconSize={'large'} />
                            </GMRoundedIcon>
                        }
                    />
                }
                backButton={
                    <CTAButton
                        type={'button'}
                        styleType={'secondary'}
                        label={Localization.Cancel}
                        onClick={() => navigate(-1)}
                        ariaLabel={Localization.Cancel}
                        id={Localization.Cancel}
                    />
                }
                submitButton={
                    <CTAButton
                        styleType={'primary'}
                        type={'submit'}
                        label={Localization.Submit}
                        id={Localization.Submit}
                        ariaLabel={Localization.Submit}
                    />
                }
            >
                <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                <Spacer />
                <GMFormPasswordField
                    name={nameof<typeof initialValues>('password')}
                    id={nameof<typeof initialValues>('password')}
                    title={Localization.Password}
                    label={Localization.Password}
                    ariaLabel={Localization.Password}
                    onChangeCallback={() => setPasswordInvalid(false)}
                    keyboardLayout={KeyboardLayouts.Password}
                    required
                    autoFocus
                />
            </GMForm>
        </Formik>
    );
};
